import { useState } from "react";
import { Link } from "react-router-dom";

import { Pill } from "../components/Badges";
import { DataTable } from "../components/DataTable";
import { Card, Empty, ErrorBox, Loading, PageHeader } from "../components/Page";
import { StatTile } from "../components/StatTile";
import { Tabs, useTab } from "../components/Tabs";
import { api } from "../lib/api";
import { day, money, num, pct } from "../lib/format";
import { useApi } from "../lib/useApi";
import { InvoiceChecks, Suppliers } from "./CostsInvoices";

interface PricePoint {
  customer: string; price_per_case: number | null; cost_per_case: number | null;
  goods_cost_per_case: number | null;
  profit_per_case: number | null; margin: number | null; stated_margin: number | null;
  stated_cost_per_case: number | null; notes: string | null;
}
interface Component {
  component: string; cost_per_bottle: number | null; sheet_cost_per_bottle: number | null;
  confirmed_id: number | null; vendor: string | null;
}
export interface Confirmed {
  id: number; component: string; labels: string[]; description: string | null; vendor: string | null;
  price: number; currency: string; usd: number | null; rate: string; said_by: string; said_on: string; said_where: string | null;
}
interface Product {
  label: string; sku: string | null; name: string; units_per_case: number;
  per_bottle: number; per_case: number; components: Component[]; prices: PricePoint[];
  realised_per_case: number | null; cases_sold: number | null;
  best_margin: number | null; best_customer: string | null;
}
interface Material {
  vendor: string; material: string; vendor_code: string | null; unit_price: number | null;
  currency: string; uom: string | null; minimum_order: number | null; lead_time_days: number | null;
  as_of: string | null; notes: string | null; cheapest: boolean;
}
interface Costs {
  as_of: string | null; products: Product[]; customers: string[]; confirmed: Confirmed[];
  materials: Material[]; vendors_without_a_quote: { vendor: string; components: string[] }[];
}

const TABS = [
  { id: "margin", label: "Cost vs price" },
  { id: "recipe", label: "What a bottle costs" },
  { id: "invoices", label: "Checked against invoices" },
  { id: "materials", label: "Vendors & materials" },
];

export function Costs() {
  const { data, error, reload, setData } = useApi<Costs>("/costs");
  const [tab, setTab] = useTab(TABS, "view");
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;

  const priced = data.products.filter((p) => p.prices.some((x) => x.margin != null));
  const best = priced.reduce<Product | null>(
    (a, b) => (a == null || (b.best_margin ?? 0) > (a.best_margin ?? 0) ? b : a), null);
  const worst = priced.reduce<Product | null>(
    (a, b) => (a == null || (b.best_margin ?? 1) < (a.best_margin ?? 1) ? b : a), null);

  return (
    <>
      <PageHeader
        title="Costs & margin"
        sub={data.as_of
          ? `What a case costs to make and what it earns from each customer, from your own COGS sheet of ${day(data.as_of)}. Every total is added up from the components rather than copied.`
          : "What a case costs to make and what it earns from each customer."} />

      {!data.products.length && !data.materials.length ? (
        <Card>
          <Empty>
            No costs yet. Upload your COGS workbook on the <Link to="/sources">Integrations</Link> page
            — the sheet with the flavours across the top and the cost rows down the side — and this
            page fills itself in. Purchase orders you upload add their prices here too.
          </Empty>
        </Card>
      ) : null}

      {data.products.length ? (
        <div className="tiles">
          <StatTile label="Products costed" value={num(data.products.length)}
            sub={`${data.customers.length} customer price list${data.customers.length === 1 ? "" : "s"}`} />
          {best ? <StatTile label="Best margin" value={pct(best.best_margin)}
            sub={`${best.label} to ${best.best_customer}`} /> : null}
          {worst ? <StatTile label="Thinnest margin" value={pct(worst.best_margin)}
            sub={`${worst.label} to ${worst.best_customer}`}
            status={(worst.best_margin ?? 1) < 0.15 ? "warning" : undefined} /> : null}
          <StatTile label="Materials quoted" value={num(data.materials.length)}
            sub={`${new Set(data.materials.map((m) => m.vendor)).size} supplier(s)`} to="/costs?view=materials" />
        </div>
      ) : null}

      <Tabs tabs={TABS} current={tab} onChange={setTab} />

      {tab === "margin" ? <Margin data={data} /> : null}
      {tab === "recipe" ? <><Recipe data={data} /><ConfirmedPrices data={data} onSaved={setData} /></> : null}
      {tab === "invoices" ? <InvoiceChecks /> : null}
      {tab === "materials" ? <Materials data={data} /> : null}
    </>
  );
}

/** One row per product per customer: what it costs, what they pay, what is left. */
function Margin({ data }: { data: Costs }) {
  const rows = data.products.flatMap((p) =>
    p.prices.filter((x) => x.price_per_case != null)
      .map((x) => ({ ...x, product: p.label, name: p.name, sku: p.sku,
                     realised: p.realised_per_case, key: `${p.label}|${x.customer}` })));
  if (!rows.length) return <Card><Empty>No customer prices in the sheet yet.</Empty></Card>;
  return (
    <Card title="Every product, to every customer">
      <DataTable testId="margin-table" rows={rows} rowKey={(r) => r.key}
        columns={[
          { key: "product", label: "Product",
            render: (r) => (r.sku ? <Link to={`/products/${r.sku}`}>{r.name}</Link> : r.product) },
          { key: "customer", label: "Customer" },
          { key: "goods_cost_per_case", label: "Goods / case", numeric: true, render: (r) => money(r.goods_cost_per_case) },
          { key: "cost_per_case", label: "Landed / case", numeric: true, render: (r) => money(r.cost_per_case) },
          { key: "price_per_case", label: "Price / case", numeric: true, render: (r) => money(r.price_per_case) },
          { key: "profit_per_case", label: "Profit / case", numeric: true, render: (r) => money(r.profit_per_case) },
          { key: "margin", label: "Margin", numeric: true,
            render: (r) => (
              <Pill tone={r.margin == null ? "neutral" : r.margin < 0.15 ? "warning" : r.margin < 0.3 ? "neutral" : "good"}>
                {pct(r.margin)}
              </Pill>) },
          { key: "realised", label: "Actually sold at", numeric: true,
            render: (r) => (r.realised == null ? "—" : money(r.realised)) },
        ]} />
      <p className="note">
        “Actually sold at” is the average a case really went out at, worked out from the sales
        invoices — not from this sheet. Where it is well under the price here, the discounting is
        real money.
      </p>
    </Card>
  );
}

/** The recipe: every cost that goes into one bottle, per flavour. */
function Recipe({ data }: { data: Costs }) {
  if (!data.products.length) return <Card><Empty>No COGS sheet uploaded yet.</Empty></Card>;
  const components = Array.from(new Set(data.products.flatMap((p) => p.components.map((c) => c.component))));
  const vendorOf = new Map<string, string>();
  data.products.forEach((p) => p.components.forEach((c) => { if (c.vendor) vendorOf.set(c.component, c.vendor); }));
  const rows = components.map((component) => ({
    component, vendor: vendorOf.get(component) ?? null,
    cells: data.products.map((p) => p.components.find((c) => c.component === component) ?? null),
  }));
  return (
    <Card title="What one bottle costs">
      <div className="table-scroll">
        <table className="grid" data-testid="recipe-table">
          <thead>
            <tr>
              <th>Component</th>
              <th>Supplier</th>
              {data.products.map((p) => <th key={p.label} className="n">{p.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.component}>
                <td>{r.component}</td>
                <td className="muted">{r.vendor ?? "—"}</td>
                {r.cells.map((c, i) => (
                  <td key={i} className="n">
                    {c?.cost_per_bottle == null ? "—" : money(c.cost_per_bottle, "USD", 4)}
                    {c?.confirmed_id ? <div className="muted small" title="A price Zach confirmed replaces the sheet's figure">
                      sheet {money(c.sheet_cost_per_bottle, "USD", 4)}</div> : null}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="totals">
              <td><strong>Per bottle</strong></td>
              <td />
              {data.products.map((p) => <td key={p.label} className="n"><strong>{money(p.per_bottle, "USD", 4)}</strong></td>)}
            </tr>
            <tr className="totals">
              <td><strong>Per case</strong></td>
              <td className="muted">× bottles in a case</td>
              {data.products.map((p) => <td key={p.label} className="n"><strong>{money(p.per_case)}</strong></td>)}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="note">
        Where a cell shows "sheet" under it, a price Zach confirmed is used instead of the sheet's
        figure (listed below). Totals here are added up from the rows above them, so they can disagree with the sheet —
        and when they do, it is raised on <Link to="/data-quality">Data quality</Link> rather than
        quietly corrected.
      </p>
    </Card>
  );
}

/** Who supplies what, and at what price. */
function Materials({ data }: { data: Costs }) {
  return (
    <>
      <Suppliers />
      <Card title="Quoted prices">
        {data.materials.length ? (
          <DataTable testId="materials-table" rows={data.materials}
            rowKey={(r) => `${r.vendor}|${r.material}`}
            columns={[
              { key: "material", label: "Material" },
              { key: "vendor", label: "Supplier" },
              { key: "vendor_code", label: "Their code", render: (r) => (r.vendor_code ? <code>{r.vendor_code}</code> : "—") },
              { key: "unit_price", label: "Unit price", numeric: true,
                render: (r) => (
                  <>
                    {money(r.unit_price, r.currency, 5)}
                    {r.cheapest && data.materials.filter((m) => m.material === r.material).length > 1
                      ? <> <Pill tone="good">cheapest</Pill></> : null}
                  </>) },
              { key: "uom", label: "Per", render: (r) => r.uom ?? "—" },
              { key: "minimum_order", label: "Min order", numeric: true, render: (r) => num(r.minimum_order) },
              { key: "lead_time_days", label: "Lead time", numeric: true,
                render: (r) => (r.lead_time_days == null ? "—" : `${r.lead_time_days} d`) },
              { key: "as_of", label: "Quoted", render: (r) => day(r.as_of) },
            ]} />
        ) : (
          <Empty>
            No quotes yet. Upload a price list, or just upload the latest purchase order from each
            supplier on the <Link to="/sources">Integrations</Link> page — every PO line records what
            that supplier charged, so the list builds itself.
          </Empty>
        )}
      </Card>

      {data.vendors_without_a_quote.length ? (
        <Card title="Named in the COGS sheet, but no quote on file">
          <DataTable testId="unquoted-table" rows={data.vendors_without_a_quote} rowKey={(r) => r.vendor}
            columns={[
              { key: "vendor", label: "Supplier" },
              { key: "components", label: "Supplies", render: (r) => r.components.join(", ") },
            ]} />
          <p className="note">
            These are the suppliers written beside the cost rows. Upload their latest purchase order
            or invoice and the price lands here automatically, dated.
          </p>
        </Card>
      ) : null}
    </>
  );
}

/** Prices Zach has stated for part of a bottle, used in place of his sheet's. */
function ConfirmedPrices({ data, onSaved }: { data: Costs; onSaved: (c: Costs) => void }) {
  const components = Array.from(new Set(data.products.flatMap((p) => p.components.map((c) => c.component))));
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ component: components[0] ?? "", labels: [] as string[], price: "", currency: "USD",
    description: "", vendor: "", said_by: "Zach", said_on: new Date().toISOString().slice(0, 10), said_where: "" });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  async function run(p: Promise<Costs>) {
    setBusy(true); setErr(null);
    try { onSaved(await p); setAdding(false); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  }
  const save = () => run(api.post<Costs>("/costs/confirmed", {
    ...form, price: Number(form.price), description: form.description || null, vendor: form.vendor || null,
    said_where: form.said_where || null }));

  return (
    <Card title="Prices Zach confirmed" actions={adding ? null : <button className="link-btn" onClick={() => setAdding(true)}>Add a price</button>}>
      {data.confirmed.length ? (
        <div className="table-scroll">
          <table className="grid" data-testid="confirmed-prices">
            <thead><tr><th>Part</th><th>Products</th><th className="n">Price</th><th className="n">Per bottle</th><th>Said</th><th /></tr></thead>
            <tbody>{data.confirmed.map((c) => (
              <tr key={c.id}>
                <td>{c.component}{c.description ? <div className="muted small">{c.description}{c.vendor ? `, ${c.vendor}` : ""}</div> : null}</td>
                <td>{c.labels.join(", ")}</td>
                <td className="n">{money(c.price, c.currency, 4)}</td>
                <td className="n">{c.usd == null ? "—" : money(c.usd, "USD", 4)}{c.currency !== "USD" ? <div className="muted small">{c.rate}</div> : null}</td>
                <td>{c.said_by}, {day(c.said_on)}{c.said_where ? <div className="muted small">{c.said_where}</div> : null}</td>
                <td><button className="btn btn-sm" disabled={busy} aria-label={`Remove ${c.component} for ${c.labels.join(", ")}`}
                  onClick={() => void run(api.del(`/costs/confirmed/${c.id}`).then(() => api.get<Costs>("/costs")))}>Remove</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : <p className="note muted">None yet. Every figure above is the COGS sheet's own.</p>}
      {adding ? (
        <form className="form-grid" onSubmit={(e) => { e.preventDefault(); void save(); }}>
          <label className="field"><span className="field-label">Part</span><select value={form.component} onChange={(e) => set("component", e.target.value)}>
            {components.map((c) => <option key={c}>{c}</option>)}</select></label>
          <fieldset className="checks"><legend>Products</legend>
            {data.products.filter((p) => p.components.some((c) => c.component === form.component)).map((p) => (
              <label key={p.label} className="check"><input type="checkbox" checked={form.labels.includes(p.label)}
                onChange={(e) => set("labels", e.target.checked ? [...form.labels, p.label] : form.labels.filter((l) => l !== p.label))} />{p.label}</label>
            ))}
          </fieldset>
          <label className="field"><span className="field-label">Price each</span><input inputMode="decimal" required value={form.price} onChange={(e) => set("price", e.target.value)} /></label>
          <label className="field"><span className="field-label">Currency</span><select value={form.currency} onChange={(e) => set("currency", e.target.value)}>
            <option>USD</option><option value="EUR">EUR (at the latest ECB rate)</option></select></label>
          <label className="field"><span className="field-label">What it is</span><input value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="silver synthetic cork" /></label>
          <label className="field"><span className="field-label">Supplier</span><input value={form.vendor} onChange={(e) => set("vendor", e.target.value)} /></label>
          <label className="field"><span className="field-label">Who said so</span><input required value={form.said_by} onChange={(e) => set("said_by", e.target.value)} /></label>
          <label className="field"><span className="field-label">On</span><input type="date" required value={form.said_on} onChange={(e) => set("said_on", e.target.value)} /></label>
          <label className="field"><span className="field-label">Where</span><input value={form.said_where} onChange={(e) => set("said_where", e.target.value)} placeholder="Upwork message" /></label>
          <div className="form-actions">
            <button className="btn btn-primary" type="submit" disabled={busy || !form.labels.length}>Use this price</button>
            <button className="btn" type="button" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </form>
      ) : null}
      {err ? <p className="down" role="alert">{err}</p> : null}
    </Card>
  );
}
